// Stroke bbox / rect geometry helpers shared by the conflict-aware Undo
// client mechanism (B' baseline canvas, docs/_wip/undo-conflict-aware-2026-07-12.md
// 「クライアント側の復元コスト」/ f5) and its property-test harness
// (`replayEquivalence.property.test.ts`).
//
// **2026-07-23 unification (f3, 着手前チェックリスト)**: this module now
// hosts THE single geometry source of truth (`growStrokeFootprintBBox` /
// `floatBBoxIntersects`) for "what rectangle could a stroke's stamps touch",
// shared with the server's authoritative bbox (`packages/server/.../
// strokeBBox.ts`, which re-exports these under its historical names —
// `StrokeBBox` / `growStrokeBBox` / `bboxIntersects` — so
// `SessionBuffer.ts` / `StrokeCancelService.ts` / `InterleavingSim.ts` are
// unaffected). Before this, the two sides carried independently-written
// (and subtly different: server padded by the RAW `halfWidth + margin`
// float, client by `Math.ceil(halfWidth) + margin`) formulas — exactly the
// "ずれたら Undo の安全性が壊れる" risk f3 called out. `strokeFootprintBbox`
// below (the whole-stroke, integer-pixel-space variant `UndoBaselineStore`
// consumes) is now a thin wrapper: it calls `growStrokeFootprintBBox` once
// over every point, then floors the result to an inclusive integer Rect
// clamped to the canvas. That flooring step is PROVABLY byte-identical to
// the pre-unification per-point-floor-then-integer-margin formula (floor
// commutes with min/max, and the shared margin is an integer via
// `Math.ceil`), so this refactor does not change `strokeFootprintBbox`'s
// output for any input — only removes the risk of the two formulas
// drifting apart in the future.
//
// The bbox formula is intentionally CONSERVATIVE (= a strict superset of
// every pixel a stroke's stamps could touch): the per-point stamp footprint
// margin (`ceil(width/2) + STROKE_BBOX_AA_MARGIN_PX`) is one px more
// generous than the rasterizer's own `stampFootprintBbox` margin of
// `ceil(width/2)+1`, to absorb AA's extra +1px ramp padding too. Sweep
// interpolation (`drawLine`) only ever produces points BETWEEN two recorded
// endpoints, so the endpoints' footprints bound the whole stroke (same
// derivation `replayEquivalence.property.test.ts` uses) — kept here as the
// single client-side source of truth so `UndoBaselineStore` can't drift
// from the equivalence harness that proves it correct.
/**
 * Margin (px) added on top of the per-point `Math.ceil(halfWidth)` padding
 * in `growStrokeFootprintBBox`, to conservatively cover AA edge ramps +
 * texture/marker bleed a couple of px beyond the analytic stamp radius (see
 * `strokeFootprintBbox`'s header for the full rationale). Shared by both
 * call sites so the pad can never drift between client and server.
 */
export const STROKE_BBOX_AA_MARGIN_PX = 2;
/**
 * Grow `bbox` (or start a fresh one when `undefined`) to cover every point
 * in `pts`, padded by `Math.ceil(halfWidth) + STROKE_BBOX_AA_MARGIN_PX` on
 * every side. THE canonical stroke-footprint growth primitive (f3) — both
 * the server's per-`stroke.segment` incremental bbox (`strokeBBox.ts`'s
 * `growStrokeBBox`, called once per segment as points stream in) and this
 * module's own `strokeFootprintBbox` (batch, whole-stroke, called once a
 * stroke has fully ended) build on this SAME function, so the two call
 * sites can never numerically drift apart again.
 *
 * `halfWidth` is the stroke's OWN half-width (`stroke.start.width / 2`) —
 * callers compute it once per stroke and pass it on every call rather than
 * re-deriving it here, since this function has no notion of a stroke's
 * lifecycle (`SessionBuffer` / `strokeFootprintBbox` own that). `Math.ceil`
 * (rather than the raw float `halfWidth`) keeps the per-point pad an
 * INTEGER — the property `strokeFootprintBbox`'s derivation below relies on.
 *
 * **Never trust client-sent geometry for the server's use of this** — the
 * server derives `pts` from its own validated `stroke.segment` payload, a
 * client cannot shrink its own bbox to dodge the conflict check.
 */
export const growStrokeFootprintBBox = (bbox, pts, halfWidth) => {
    const pad = Math.ceil(halfWidth) + STROKE_BBOX_AA_MARGIN_PX;
    let minX = bbox?.minX ?? Number.POSITIVE_INFINITY;
    let minY = bbox?.minY ?? Number.POSITIVE_INFINITY;
    let maxX = bbox?.maxX ?? Number.NEGATIVE_INFINITY;
    let maxY = bbox?.maxY ?? Number.NEGATIVE_INFINITY;
    for (const p of pts) {
        const x0 = p.x - pad;
        const x1 = p.x + pad;
        const y0 = p.y - pad;
        const y1 = p.y + pad;
        if (x0 < minX)
            minX = x0;
        if (y0 < minY)
            minY = y0;
        if (x1 > maxX)
            maxX = x1;
        if (y1 > maxY)
            maxY = y1;
    }
    return { minX, minY, maxX, maxY };
};
/** Axis-aligned float bbox intersection test (touching edges count as intersecting). */
export const floatBBoxIntersects = (a, b) => a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY;
/**
 * Conservative bbox of a stroke's stamp footprint, clamped to the canvas.
 * Returns `null` for an empty point list or a stroke that lands entirely
 * off-canvas.
 *
 * Built on `growStrokeFootprintBBox` (f3 unification, see module header):
 * grows a single float bbox over every point, then floors both the min and
 * max corners to integer pixels. This is byte-identical to the
 * pre-unification "floor each point first, then apply an integer margin"
 * formula — `Math.floor` commutes with `min`/`max`
 * (`floor(min(a,b)) === min(floor(a), floor(b))`, and symmetrically for
 * `max`), and `growStrokeFootprintBBox`'s pad is an integer (`Math.ceil`),
 * so `floor(minX_i - pad)` per point vs `floor(min_i(x_i - pad))` for the
 * unioned float bbox produce the exact same integer.
 */
export const strokeFootprintBbox = (points, penWidth, canvasWidth, canvasHeight) => {
    if (points.length === 0)
        return null;
    const float = growStrokeFootprintBBox(undefined, points, penWidth / 2);
    const x0 = Math.max(0, Math.floor(float.minX));
    const y0 = Math.max(0, Math.floor(float.minY));
    const x1 = Math.min(canvasWidth - 1, Math.floor(float.maxX));
    const y1 = Math.min(canvasHeight - 1, Math.floor(float.maxY));
    if (x0 > x1 || y0 > y1)
        return null;
    return { x0, y0, x1, y1 };
};
/**
 * Conservative bbox of a `whiteboard.imagepaste`'s destination rectangle,
 * clamped to the canvas (undo-conflict-aware-2026-07-12.md「第二弾設計確定」
 * imagepaste × Undo 穴埋め — client-side ingest into `UndoBaselineStore`).
 * Mirrors `EngineCore.applyImagepaste`'s own clamp math (`Math.floor(x)` /
 * `Math.floor(y)` for the min corner), except the max corner uses
 * `Math.ceil(width)` / `Math.ceil(height)` (vs `applyImagepaste`'s raw
 * `width`/`height`, unfloored) so this always returns a conservative INTEGER
 * superset even for a (protocol-legal but atypical) fractional `width`/
 * `height` — same "conservative, not exact" philosophy as
 * `strokeFootprintBbox`. In practice `width`/`height` are integer pixel
 * counts (client reads them via the File API, protocol.ts comment on
 * `whiteboard.imagepaste-request`), so the two formulas coincide.
 *
 * Returns `null` when the destination rect is entirely off-canvas.
 */
export const imagepasteFootprintBbox = (args, canvasWidth, canvasHeight) => {
    const x0 = Math.max(0, Math.floor(args.x));
    const y0 = Math.max(0, Math.floor(args.y));
    const x1 = Math.min(canvasWidth - 1, Math.floor(args.x) + Math.ceil(args.width) - 1);
    const y1 = Math.min(canvasHeight - 1, Math.floor(args.y) + Math.ceil(args.height) - 1);
    if (x0 > x1 || y0 > y1)
        return null;
    return { x0, y0, x1, y1 };
};
/** Whether two (possibly `null`) rects overlap. `null` never intersects anything. */
export const rectsIntersect = (a, b) => {
    if (a === null || b === null)
        return false;
    return a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1;
};
/** Union of two (possibly `null`) rects. */
export const unionRect = (a, b) => {
    if (a === null)
        return b;
    if (b === null)
        return a;
    return {
        x0: Math.min(a.x0, b.x0),
        y0: Math.min(a.y0, b.y0),
        x1: Math.max(a.x1, b.x1),
        y1: Math.max(a.y1, b.y1),
    };
};
/** Union of a list of rects, or `null` if the list is empty. */
export const unionRects = (rects) => {
    let out = null;
    for (const r of rects)
        out = unionRect(out, r);
    return out;
};
//# sourceMappingURL=strokeGeometry.js.map